import { useSyncExternalStore } from 'react';
import { Modal, Popover } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  getOpenTabServerSnapshot,
  getOpenTabSnapshot,
  requestCloseOpenTab,
  subscribeOpenTabs,
  type OpenTabInfo,
} from '@/utils/openTabRegistry';

const tabLabel = (tab: OpenTabInfo) => tab.title || tab.path || 'Tab';

export function OpenTabCount() {
  const { t } = useTranslation();
  const { count, tabs } = useSyncExternalStore(subscribeOpenTabs, getOpenTabSnapshot, getOpenTabServerSnapshot);

  if (count < 2) return null;
  const summary = t('bsc.label.sotabdangonline', '{{count}} tab đang online', { count });
  const askClose = (tab: OpenTabInfo) => {
    Modal.confirm({
      title: t('bsc.title.dongtab', 'Đóng tab này?'),
      content: t(
        'bsc.msg.xacnhandongtab',
        'Tab "{{name}}" sẽ được chuyển về trang trống của trình duyệt.',
        { name: tabLabel(tab) },
      ),
      okText: t('bsc.btn.dongtab', 'Đóng tab'),
      cancelText: t('bsc.btn.huy', 'Hủy'),
      okButtonProps: { danger: true },
      onOk: () => requestCloseOpenTab(tab.tabId),
    });
  };

  return (
    <Popover
      trigger="click"
      placement="bottomRight"
      content={(
        <div className="w-[280px]">
          <div className="mb-2 text-sm font-semibold text-[#1c252e]">{summary}</div>
          <div className="mb-2 text-xs text-[#8c8c8c]">
            {t(
              'bsc.msg.tabonlinecunguser',
              'Mọi tab đang online của tài khoản này, gồm trình duyệt khác và máy khác.',
            )}
          </div>
          <div className="flex max-h-[280px] flex-col gap-1 overflow-auto">
            {tabs.map((tab, index) => (
              <div key={tab.tabId} className="rounded-md bg-[#F4F6F8] px-2 py-1.5">
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate text-sm text-[#1c252e]">{`${index + 1}. ${tabLabel(tab)}`}</span>
                  {tab.self ? (
                    <span className="shrink-0 text-[11px] text-[#1677ff]">
                      {t('bsc.label.tabnay', 'Tab này')}
                    </span>
                  ) : (
                    <button
                      type="button"
                      className="shrink-0 text-[11px] font-medium text-[#cf1322]"
                      onClick={() => askClose(tab)}
                    >
                      {t('bsc.btn.dong', 'Đóng')}
                    </button>
                  )}
                </div>
                <div className="truncate text-[11px] text-[#8c8c8c]">
                  {[
                    tab.browser,
                    tab.sameMachine ? t('bsc.label.maynay', 'Máy này') : t('bsc.label.maykhac', 'Máy khác'),
                    tab.ip,
                    tab.path,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    >
      <button
        type="button"
        className="flex h-9 cursor-pointer items-center rounded-full bg-[#FFF7E6] px-3 text-xs font-semibold text-[#D46B08]"
        aria-label={summary}
      >
        {t('bsc.label.sotabdangmo', '{{count}} tab đang mở', { count })}
      </button>
    </Popover>
  );
}
